<script setup>
import fiveMixin from '@/composables/fiveMixin';
import useFunctions from '@/composables/useFunctions';
import useAuth from '@/composables/useAuth';
import { addContentLogAPI } from '@/api/logServiceApi';
import { getGroupUserlist } from '@/api/UserGroup';
import { getLatestSajuCache, setLatestSajuCache } from '@/composables/useMainPageCache';
import AlertComponent from '@/components/common/Alert.vue';
import ConfirmComponent from '@/components/common/Confirm.vue';
import PopQrBox from '@/components/saju/PopQrBox.vue';
import PopShareBox from '@/components/saju/PopShareBox.vue';

const props = defineProps({
  prLangMode: {
    type: String,
    default: '',
  },
  prPgmode: {
    type: String,
    default: '',
  },
  prData: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits();

const router = useRouter();
const { ...five } = fiveMixin();
const { ...useFun } = useFunctions();
const { userInfo } = useAuth();

const isPopShareBox = ref(false);
const isPopQrBox = ref(false);

const dialogAlert = ref(null);
const dialogConfirm = ref(null);
const dialogTitle = ref('');
const dialogTitle2 = ref('');
const dialogText = ref('');
const dialogNextUrl = ref('');

const shareOpen = () => {
  addContentLogAPI(props.prPgmode === 'rs' ? 'sl_btn_share' : 'manse_btn_share');
  if (Object?.keys(props.prData).length < 1) {
    showDialogAlert('사주조회 후 이용하여 주시기 바랍니다.', '', '/saju-input');
    return;
  }
  isPopShareBox.value = true;
};

const closePopShareBox = () => {
  isPopShareBox.value = false;
};

const qrOpen = () => {
  addContentLogAPI(props.prPgmode === 'rs' ? 'sl_btn_download_app' : 'manse_btn_download_app');
  isPopQrBox.value = true;
};

const closePopQrBox = () => (isPopQrBox.value = false);

const showDialogAlert = (title, text = '', nextUrl = '') => {
  dialogTitle.value = title;
  dialogText.value = text;
  dialogNextUrl.value = nextUrl;
  dialogAlert.value.openAlertDialog();
};

const showDialogConfirm = (title, title2 = '', text = '', onConfirm = () => {}) => {
  dialogTitle.value = title;
  dialogTitle2.value = title2;
  dialogText.value = text;
  dialogNextUrl.value = onConfirm; // 함수로 전달

  dialogConfirm.value.openConfirmDialog();
};

const favList = ref(getLatestSajuCache() || {});

const fetchFavList = async () => {
  try {
    const param = {};
    param.size = 3;
    param.page = 1;
    const data = await getGroupUserlist(param); // 저장목록 조회
    favList.value = data?.content;
    setLatestSajuCache(favList.value);
  } catch (error) {
    console.error('Failed to load users:', error);
  } finally {
  }
};

onMounted(async () => {
  if (userInfo) {
    fetchFavList();
  }
});

function goView(id) {
  router.push(`/saju-result/${id}`);
}

function print() {
  addContentLogAPI(props.prPgmode === 'rs' ? 'sl_btn_print' : 'manse_btn_print');

  if (!userInfo) {
    const nextUrl = '/auth/login';
    showDialogConfirm('로그인 후 편리하게 이용할 수 있습니다.', '', '로그인', () =>
      router.push(nextUrl),
    );
    return;
  } else {
    emit('printclick');
  }
}

function goLogin() {
  router.push(`/auth/login`);
}
</script>

<template>
  <div>
    <section class="manse-right-box" :class="{ modeRs: prPgmode === 'rs' }">
      <div class="manse-floating">
        <section v-if="prPgmode !== 'rs'" class="latest-box">
          <div class="tit-box">최근 등록사주</div>
          <div v-if="favList && Object.keys(favList).length > 0" class="">
            <div
              v-for="(item, index) in favList"
              :key="index"
              class="row-item-box"
              @click="goView(item.guestUserId)"
            >
              <div class="name-box">
                <span class="tit1"
                  >{{ five.formatGanJi(item?.sajugangi, 2) }}
                  {{ five.formatGanJi(item?.sajugangi, 6) }}</span
                >
                <span class="tit2">({{ useFun.getGenderTxt(item.gender) }})</span>
              </div>
              <div class="year-box">
                {{ useFun.getYear(item.birth) }}년 / {{ useFun.getAge(item.birth) }}세
              </div>
            </div>
          </div>
          <div v-else class="fav-none-box">
            <div class="txt-none">최근 등록된<br />사주가 <br />없습니다.</div>
            <button v-if="!userInfo" class="btn-line" @click="goLogin()">로그인</button>
          </div>
        </section>
        <section class="app-down-box">
          <button class="btn-down-right" @click="shareOpen()">
            <i class="icon-share" />공유하기
          </button>
          <button class="btn-down-right" @click="print()"><i class="icon-print" />출력하기</button>
          <button class="btn-down-app" @click="qrOpen()"><i class="icon-down" />앱 다운로드</button>
        </section>
      </div>
    </section>

    <!-- 공유하기 -->
    <PopShareBox
      ref="dialogShare"
      v-if="isPopShareBox"
      :pr-data="prData"
      :prPgmode="prPgmode"
      @close="closePopShareBox()"
      @click:outside="closePopShareBox()"
      @share="share"
    />

    <Teleport to="body">
      <AlertComponent
        ref="dialogAlert"
        :title="dialogTitle"
        :text="dialogText"
        :nextUrl="dialogNextUrl"
      />
      <ConfirmComponent
        ref="dialogConfirm"
        :title="dialogTitle"
        :title2="dialogTitle2"
        :text="dialogText"
        :nextUrl="dialogNextUrl"
      />
    </Teleport>

    <PopQrBox v-if="isPopQrBox" @closePopQrBox="closePopQrBox()" />
  </div>
</template>
